import { FC, ReactNode } from 'react';
import { makeStyles } from 'tss-react/mui';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { InfoOutlined } from '@mui/icons-material';

const useStyles = makeStyles()({
  label: {
    padding: 0,
    margin: 0,
    fontSize: 13,
    letterSpacing: 0,
    fontWeight: 'bold',
    color: '#656565',
    display: 'flex',
    alignItems: 'center',
    gridGap: '0.5rem',
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
  },
  informationText: {
    padding: 0,
    margin: '0 0 0 0',
    fontSize: 12,
    color: '#656565',
    width: '100%',
  },
  tooltipIcon: {
    fontSize: '16px',
    cursor: 'pointer',
  },
  topTitle: {
    height: 35,
  },
  introLineTopTitle: {
    height: 75,
  },
});

const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'spacing',
})<{ spacing?: number }>(({ spacing }) => ({
  position: 'relative',
  paddingTop: spacing,
  paddingBottom: spacing,
}));

type FormLabelProps = {
  title: string;
  informationText?: string | ReactNode;
  tooltipText?: string | number | ReactNode;
  topTitle?: boolean;
  introLineTopTitle?: boolean;
  tooltipMaxWidth?: number;
  className?: string;
  labelColor?: string;
  spacing?: number;
  wrapLabel?: boolean;
};

const FormLabel: FC<FormLabelProps> = ({
  title,
  informationText,
  tooltipText,
  tooltipMaxWidth = 300,
  topTitle, // topTitle prop aligns labels to match the top of the input fields (ck editor)
  introLineTopTitle,
  className,
  labelColor,
  spacing,
  wrapLabel,
}) => {
  const { classes, cx } = useStyles();

  const tooltip = tooltipText ? (
    <Tooltip
      leaveDelay={250}
      arrow
      title={tooltipText}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: tooltipMaxWidth,
          },
        },
      }}
    >
      <InfoOutlined className={classes.tooltipIcon} />
    </Tooltip>
  ) : null;

  return (
    <Container className={className} spacing={spacing}>
      <label
        className={cx(
          classes.label,
          topTitle && classes.topTitle,
          introLineTopTitle && classes.introLineTopTitle,
        )}
        style={{
          color: labelColor,
          ...(wrapLabel && { whiteSpace: 'normal', flexWrap: 'wrap' }),
        }}
      >
        {title}
        {tooltip}
      </label>
      {informationText && (
        <span className={classes.informationText}>{informationText}</span>
      )}
    </Container>
  );
};

export default FormLabel;
